// packages/frontend/src/hooks/data/ahaMoment.ts
//
// Activation "aha moment" trigger (onboarding hero-journey spec, Phase 0).
// After each delivered chat response, attempt (fire-and-forget) to record the
// user's aha moment server-side: first response with workspace content. The
// server is the once-only source of truth (`state.ahaReachedAt`); the guard
// here only bounds request volume.
//
// Module-level budget so the desktop panel and the mobile Bird page (which
// mounts outside Layout) share one budget per page load: stop as soon as the
// server settles it (`recorded` / `already_recorded` / `not_enrolled`), or
// after 3 attempts. `no_content` is the only retryable status — the workspace
// may gain content later in the session.
//
// The API modules are imported dynamically so this module (pulled in by
// useAgentChat) doesn't drag the full API/Firebase graph into chat unit
// tests — the same isolation concern documented for agentChatReducer.
// recordAhaMoment is called from workspace-admin.api with apiService as
// the HttpClient — the standard endpoint-fn idiom.

import { FEATURE_FLAGS } from '@bower/shared/constants/featureFlags';

let ahaAttemptsRemaining = 3;

/**
 * Test-only reset — real page loads reset by re-evaluating the module.
 *
 * @internal
 */
export const resetAhaAttempts = (): void => {
  ahaAttemptsRemaining = 3;
};

export const maybeRecordAhaMoment = (): Promise<void> => {
  if (ahaAttemptsRemaining <= 0) return Promise.resolve();
  ahaAttemptsRemaining -= 1;
  return Promise.all([
    import('../../services/api.service'),
    import('../../services/api/workspace-admin.api'),
    import('../../lib/queryClient'),
    import('posthog-js'),
  ])
    .then(([apiSvc, adminApi, qc, posthogModule]) => {
      // Cheap client-side short-circuit: if the cached checklist already
      // shows the aha moment recorded (or the user isn't enrolled at all),
      // skip the POST — the server would just answer `already_recorded` /
      // `not_enrolled`. The server stays the once-only source of truth;
      // this only avoids a known-redundant request per chat message.
      const cachedChecklist = qc.queryClient.getQueryData<{
        state?: { ahaReachedAt?: string | null } | null;
      }>(qc.ONBOARDING_CHECKLIST_KEY);
      // Dismissed-checklist users never fetch the checklist query, so also
      // consult the /users/me cache, whose user row carries the same state.
      const cachedUser = qc.queryClient.getQueryData<{
        onboardingChecklist?: { ahaReachedAt?: string | null } | null;
        setupListEnabled?: boolean;
      }>(qc.queryKeys.users.current);
      if (cachedChecklist?.state?.ahaReachedAt || cachedUser?.onboardingChecklist?.ahaReachedAt) {
        ahaAttemptsRemaining = 0;
        return null;
      }
      // The client flag, or the server's stamped enrolment (a build with no
      // PostHog key never evaluates the client flag).
      const unifiedSetup =
        posthogModule.default.isFeatureEnabled(FEATURE_FLAGS.ONBOARDING_UNIFIED_SETUP) === true ||
        cachedUser?.setupListEnabled === true;
      return [apiSvc, adminApi, unifiedSetup] as const;
    })
    .then((mods) => {
      if (!mods) return;
      const [{ apiService }, { recordAhaMoment }, unifiedSetup] = mods;
      return recordAhaMoment(apiService).then((status) => {
        // On the unified setup list the wizard's Ask runs before the per-user
        // enrolment at Finish, so `not_enrolled` is not final there: a later
        // chat in this page load can still record it, within the same budget.
        const retryNotEnrolled = status === 'not_enrolled' && unifiedSetup;
        if (status !== 'no_content' && !retryNotEnrolled) ahaAttemptsRemaining = 0;
      });
    })
    .catch(() => {
      // Fire-and-forget: analytics must never surface in the chat UX. A
      // failed attempt burns budget; the next page load starts fresh.
    });
};
